import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import SecuritySection from '../../ClientPages/ClientSettings/components/SecuritySection';
import BookingPreferencesSection from './components/BookingPreferencesSection';
import PayoutSettingsSection, { PayoutData } from './components/PayoutSettingsSection';
import PetPreferencesSection, { PetPreferencesData } from './components/PetPreferencesSection';
import ProfileSection, { ProfileData } from './components/ProfileSection';
import ServicesPricingSection from './components/ServicesPricingSection';
import SitterNotificationsSection from './components/SitterNotificationsSection';
import YourHomeSection, { HomeData } from './components/YourHomeSection';

import { useDeleteSitterProfile } from '../../../hooks/sitter/useDeleteSitterProfile';
import { useGetSitterProfile } from '../../../hooks/sitter/useGetSitterProfile';
import { useSetPricing } from '../../../hooks/sitter/useSetPricing';
import { useUpdateSitterProfile } from '../../../hooks/sitter/useUpdateSitterProfile';
import AccountManagementSection from '../../ClientPages/ClientSettings/components/AccountManagementSection';

import {
  BookingPreferences,
  SecuritySettings,
  SitterEmailNotifications,
  SitterPushNotifications,
  SitterSmsNotifications,
} from 'shared/types';

const initialPetPreferences: PetPreferencesData = {
  dogs: {
    sizes: ['small', 'medium', 'large'],
    ages: ['adult', 'senior'],
    maxNumber: 3,
  },
  cats: { accepted: true, maxNumber: 2 },
  otherPets: ['birds', 'rabbits'],
  requirements: { houseTrained: true, vaccinated: true, spayedNeutered: false },
};

const initialHome: HomeData = {
  type: 'house',
  size: '3BR',
  fencedYard: true,
  hasPets: true,
  petsDescription: '1 friendly golden retriever',
  hasChildren: false,
  childrenAges: '',
  smoking: false,
  photos: [],
};

const initialBookingPrefs: BookingPreferences = {
  minNotice: '1-day',
  maxAdvance: '3-months',
  minStay: '1-night',
  maxStay: 'no-limit',
  autoAccept: false,
};

const initialEmailNotifications: SitterEmailNotifications = {
  bookingRequests: true,
  confirmations: true,
  reminders: true,
  messages: true,
  payments: true,
  reviews: true,
  weeklySummary: false,
};

const initialPushNotifications: SitterPushNotifications = {
  requests: true,
  messages: true,
  updates: true,
};

const initialSmsNotifications: SitterSmsNotifications = {
  critical: false,
};

const initialPayout: PayoutData = {
  bankAccount: {
    holderName: 'Jose Lopez',
    bankName: 'Chase Bank',
    accountLast4: '1234',
    routingLast4: '5678',
    type: 'Checking',
  },
  autoPayout: true,
  threshold: '$100',
  taxVerified: true,
};

const initialSecurity: SecuritySettings = {
  twoFactorEnabled: false,
  loginAlerts: true,
  activeSessions: [
    { id: '1', device: 'Chrome on Mac', current: true },
    { id: '2', device: 'iPhone', current: false, lastActive: '2 days ago' },
  ],
};

// ─── Component ────────────────────────────────────────────────────────────────

import SitterHeader from '../SitterHeader/SitterHeader';

const SitterSettings: React.FC = () => {
  const navigate = useNavigate();

  const { data: sitterProfile } = useGetSitterProfile();
  const { mutate: updateSitterProfile } = useUpdateSitterProfile();
  const { mutate: deleteSitterProfile } = useDeleteSitterProfile();
  const { mutate: setPricing } = useSetPricing();

  const profileData: ProfileData = {
    firstName: sitterProfile?.firstName ?? '',
    lastName: sitterProfile?.lastName ?? '',
    email: sitterProfile?.email ?? '',
    phone: sitterProfile?.phone ?? '',
    location: { city: '', state: '', zipCode: '' }, // still mock
    photoUrl: sitterProfile?.photoUrl ?? null,
    bio: sitterProfile?.bio ?? '',
    experience: sitterProfile?.experience ?? null,
    languages: [], // still mock
  };

  const handleProfileSave = (data: ProfileData) => {
    updateSitterProfile({
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone,
      bio: data.bio,
      experience: data.experience,
      profilePhotoUrl: data.photoUrl,
    });
    autoSave();
  };

  const handleServicesSave = (rows: { service: string; priceMin: number; priceMax: number }[]) => {
    setPricing({ items: rows }, { onSuccess: autoSave });
  };

  const handleDelete = () => {
    deleteSitterProfile(undefined, {
      onSuccess: () => navigate('/register'),
      onError: (error) => console.error('Failed to delete:', error),
    });
  };

  const [petPrefs, setPetPrefs] = useState<PetPreferencesData>(initialPetPreferences);
  const [home, setHome] = useState<HomeData>(initialHome);
  const [bookingPrefs, setBookingPrefs] = useState<BookingPreferences>(initialBookingPrefs);
  const [emailNotifs, setEmailNotifs] =
    useState<SitterEmailNotifications>(initialEmailNotifications);
  const [pushNotifs, setPushNotifs] = useState<SitterPushNotifications>(initialPushNotifications);
  const [smsNotifs, setSmsNotifs] = useState<SitterSmsNotifications>(initialSmsNotifications);
  const [payout, setPayout] = useState<PayoutData>(initialPayout);
  const [security, setSecurity] = useState<SecuritySettings>(initialSecurity);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const autoSave = () => setSnackbarOpen(true);

  // ── Notification handlers ──
  const handleEmailChange = (key: keyof SitterEmailNotifications, value: boolean) => {
    setEmailNotifs((prev) => ({ ...prev, [key]: value }));
    autoSave();
  };

  const handlePushChange = (key: keyof SitterPushNotifications, value: boolean) => {
    setPushNotifs((prev) => ({ ...prev, [key]: value }));
    autoSave();
  };

  const handleSmsChange = (key: keyof SitterSmsNotifications, value: boolean) => {
    setSmsNotifs((prev) => ({ ...prev, [key]: value }));
    autoSave();
  };

  // ── Security handlers ──
  const handleToggle2FA = () => {
    setSecurity((prev) => ({
      ...prev,
      twoFactorEnabled: !prev.twoFactorEnabled,
    }));
    autoSave();
  };

  const handleLoginAlertChange = (value: boolean) => {
    setSecurity((prev) => ({ ...prev, loginAlerts: value }));
    autoSave();
  };

  const handleLogoutSession = (id: string) => {
    setSecurity((prev) => ({
      ...prev,
      activeSessions: prev.activeSessions.filter((s) => s.id !== id),
    }));
    autoSave();
  };

  const handleLogoutAllSessions = () => {
    setSecurity((prev) => ({
      ...prev,
      activeSessions: prev.activeSessions.filter((s) => s.current),
    }));
    autoSave();
  };

  const handlePetPrefsSave = (data: PetPreferencesData) => {
    setPetPrefs(data);
    autoSave();
  };

  const handleHomeSave = (data: HomeData) => {
    setHome(data);
    autoSave();
  };

  const handleBookingPrefsSave = (data: BookingPreferences) => {
    setBookingPrefs(data);
    autoSave();
  };

  const handlePayoutSave = (data: PayoutData) => {
    setPayout(data);
    autoSave();
  };

  return (
    <Box sx={{ backgroundColor: '#FAF8F5', minHeight: '100vh' }}>
      <SitterHeader />
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Page Header */}
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
          Settings
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Manage your profile, services, and preferences
        </Typography>

        {/* Section 1: Profile */}
        <ProfileSection data={profileData} onSave={handleProfileSave} />

        {/* Section 2: Services & Pricing */}
        <ServicesPricingSection data={sitterProfile?.pricing ?? []} onSave={handleServicesSave} />

        {/* Section 3: Pet Preferences */}
        <PetPreferencesSection data={petPrefs} onSave={handlePetPrefsSave} />

        {/* Section 4: Your Home */}
        <YourHomeSection
          data={{
            ...home,
            photos: sitterProfile?.galleryImages ?? [],
          }}
          onSave={handleHomeSave}
        />

        {/* Section 5: Booking Preferences */}
        <BookingPreferencesSection data={bookingPrefs} onSave={handleBookingPrefsSave} />

        {/* Section 6: Notifications */}
        <SitterNotificationsSection
          emailNotifications={emailNotifs}
          pushNotifications={pushNotifs}
          smsNotifications={smsNotifs}
          onEmailChange={handleEmailChange}
          onPushChange={handlePushChange}
          onSmsChange={handleSmsChange}
        />

        {/* Section 7: Payout Settings */}
        <PayoutSettingsSection data={payout} onSave={handlePayoutSave} />

        {/* Section 8: Security & Account */}
        <SecuritySection
          settings={security}
          onToggle2FA={handleToggle2FA}
          onLoginAlertChange={handleLoginAlertChange}
          onLogoutSession={handleLogoutSession}
          onLogoutAllSessions={handleLogoutAllSessions}
        />
        <AccountManagementSection
          onExportData={() => console.log('Export requested')}
          onDeactivate={() => console.log('Deactivate requested')}
          onDelete={handleDelete}
        />

        {/* Auto-save Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={2000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert severity="success" onClose={() => setSnackbarOpen(false)} sx={{ minWidth: 200 }}>
            Settings saved
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default SitterSettings;
